import React, { FC, Suspense, useState } from 'react';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import { T } from 'src/shared/helpers';
import type { addPrintingMethodPanel_PrintTypesListQuery } from './__generated__/addPrintingMethodPanel_PrintTypesListQuery.graphql';
import { PanelLinesSkeleton } from '../common/skeletons';

import {
    StackedBody,
    StackedFooterButtons,
    FooterButton,
    PanelItem,
    ItemDot,
    ItemName,
    ItemActionSuccess,
    PanelItemSpaced,
    ItemNameAction,
    EmptyState
} from '../common/styled';

export interface AddPrintingMethodPanelProps {
    productId: string;
    existingMethodIds: string[];
    onBack: () => void;
    onCreateNew: (nextNumber: number) => void;
    onAssigned: () => void;
}

const AddPrintingMethodPanelContent: FC<AddPrintingMethodPanelProps> = ({ productId, existingMethodIds, onBack, onCreateNew, onAssigned }) => {
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addPrintingMethodPanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addPrintingMethodPanel_PrintTypesListQuery>(
        graphql`
            query addPrintingMethodPanel_PrintTypesListQuery {
                printTypes(first: 200) {
                    edges {
                        node {
                            id
                            printTypeID
                            name
                        }
                    }
                }
            }
        `,
        {}
    );

    const allMethods = React.useMemo(() => {
        const edges = data?.printTypes?.edges || [];
        return edges.map((edge: { node: { id: string; printTypeID: number; name: string } }) => ({
            id: edge.node.id,
            numericId: String(edge.node.printTypeID),
            name: edge.node.name
        }));
    }, [data]);

    const getNextPrintMethodNumber = () => {
        const numbers = allMethods.map((printMethod: { name: string }) => {
            const match = printMethod.name.match(/^Printing Method (\d+)$/);
            return match ? parseInt(match[1]) : 0;
        });
        return Math.max(0, ...numbers) + 1;
    };

    const [assigning, setAssigning] = useState<string | null>(null);

    const [commitAssign] = useMutation(graphql`
        mutation addPrintingMethodPanel_AssignMutation($input: AddOrRemoveAssignmentPrintTypeToProductsInput!) {
            addOrRemoveAssignmentPrintTypeToProducts(input: $input)
        }
    `);

    const handleAssign = (printMethodId: string) => {
        setAssigning(printMethodId);
        commitAssign({
            variables: { input: { printTypeID: printMethodId, modelSidesToAssignsDto: [{ modelID: productId, sides: [] }] } },
            onCompleted: () => {
                setAssigning(null);
                onAssigned();
            },
            onError: () => {
                setAssigning(null);
            }
        });
    };

    const availableMethods = allMethods.filter((printMethod: { id: string }) => !existingMethodIds.includes(printMethod.id));

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add printing method', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableMethods.length === 0 && <EmptyState>{T._('All printing methods are already assigned', 'Admin')}</EmptyState>}
                {availableMethods.map((printMethod: { id: string; numericId: string; name: string }) => (
                    <PanelItem key={printMethod.id} $clickable={assigning === null} onClick={() => assigning === null && handleAssign(printMethod.id)}>
                        <ItemDot $color='#3b82f6' />
                        <ItemName>{printMethod.name}</ItemName>
                        <ItemActionSuccess>{assigning === printMethod.id ? T._('Assigning...', 'Admin') : T._('Assign', 'Admin')}</ItemActionSuccess>
                    </PanelItem>
                ))}
                <PanelItemSpaced $clickable onClick={() => onCreateNew(getNextPrintMethodNumber())}>
                    <ItemDot $color='#d1d5db' />
                    <ItemNameAction>
                        <Icon>
                            <PlusIcon />
                        </Icon>
                        {T._('Create new', 'Admin')}
                    </ItemNameAction>
                </PanelItemSpaced>
            </StackedBody>
        </Drawer>
    );
};

export const AddPrintingMethodPanel: FC<AddPrintingMethodPanelProps> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add printing method', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <PanelLinesSkeleton />
                    </StackedBody>
                </Drawer>
            }
        >
            <AddPrintingMethodPanelContent {...props} />
        </Suspense>
    );
};
